import { useState, useEffect } from 'react';
import {
  useBudgetSuggestionRepository, MODE, CustomBudget,
} from '@bingads-webui-campaign-react/budget-suggestion-v2';
import { CampaignTypes } from '@bingads-webui-campaign-react/constants';
import _ from 'underscore';

export const useBudgetSuggestionData = ({
  cpa,
  roas,
  bidStrategy,
  currency,
  campaign = {},
  assetGroup,
  budgetTier,
  budgetAmount,
  isBudgetValid,
  enableBudgetSuggestion,
  triggerBudgetSuggestion,
  onBudgetSuggestionDone, // set triggerBudgetSuggestion as false
  logger,
}) => {
  const [isBudgetSuggestionLoaded, setIsBudgetSuggestionLoaded] = useState(false);
  const [isBudgetSuggestionLoading, setIsBudgetSuggestionLoading] = useState(false);
  const [budgetSuggestionData, setBudgetSuggestionData] = useState({});
  const [customPerformanceData, setCustomPerformanceData] = useState({});

  const {
    loadBudget,
    loadBudgetPartial,
    saveBudget,
    fetchCustomBudgetPerformanceData,
  } = useBudgetSuggestionRepository({
    campaignId: campaign.Id,
    mode: MODE.CREATION,
    campaignType: CampaignTypes.PerformanceMax,
    currency,
    campaign,
    assetGroup,
  });

  useEffect(() => {
    if (enableBudgetSuggestion && !isBudgetSuggestionLoaded
      && _.isEmpty(budgetSuggestionData) && !_.isEmpty(campaign)
    ) {
      setIsBudgetSuggestionLoading(true);

      loadBudget().then((data) => {
        setBudgetSuggestionData(data);
      }).catch((err) => {
        logger.trace(`failed to load budget with error: ${JSON.stringify(err)}`);
      }).finally(() => {
        setIsBudgetSuggestionLoading(false);
        setIsBudgetSuggestionLoaded(true);
        onBudgetSuggestionDone();
      });
    }
  }, [enableBudgetSuggestion, budgetSuggestionData, loadBudget, logger,
    isBudgetSuggestionLoaded, campaign, onBudgetSuggestionDone]);

  useEffect(() => {
    if (enableBudgetSuggestion && loadBudgetPartial
      && (
        (bidStrategy && campaign && campaign.BiddingScheme.Type !== bidStrategy)
        || triggerBudgetSuggestion
      )
    ) {
      setIsBudgetSuggestionLoading(true);

      loadBudgetPartial({
        BiddingScheme: {
          Type: bidStrategy,
          TargetCpa: cpa,
          TargetRoas: roas,
        },
      }).then((budgetSuggestions) => {
        setBudgetSuggestionData(budgetSuggestions);
        setIsBudgetSuggestionLoading(false);
      }).catch((err) => {
        logger.trace(`failed to load budget partial with error: ${JSON.stringify(err)}`);
        setIsBudgetSuggestionLoading(false);
      }).finally(() => {
        onBudgetSuggestionDone();
      });
    }
  }, [enableBudgetSuggestion, bidStrategy, campaign, cpa, loadBudgetPartial,
    roas, triggerBudgetSuggestion, onBudgetSuggestionDone, logger]);

  useEffect(() => {
    if (enableBudgetSuggestion && budgetTier === CustomBudget && budgetAmount && !triggerBudgetSuggestion) {
      if (fetchCustomBudgetPerformanceData && isBudgetValid && parseInt(budgetAmount, 10) > 0) {
        setCustomPerformanceData({ isLoading: true });
        fetchCustomBudgetPerformanceData({ budgetAmount })
          .then(result => setCustomPerformanceData({ isLoading: false, ...result }))
          .catch((err) => {
            logger.trace(`failed to fetch custom budget performance data with error: ${JSON.stringify(err)}`);
            setCustomPerformanceData({ isLoading: false });
          });
      }
    }
  }, [enableBudgetSuggestion, budgetAmount, budgetTier, isBudgetValid,
    fetchCustomBudgetPerformanceData, logger, triggerBudgetSuggestion]);

  return {
    isBudgetSuggestionLoading,
    budgetSuggestionData,
    customPerformanceData,
    saveBudgetData: saveBudget,
  };
};
